import React, { useState, useMemo } from "react";

// ─── ECONOMIC CALENDAR ────────────────────────────────────────
// Event types, colors, and 2025-2026 calendar data
// Events are drawn on CandleChart as vertical lines + flags
// Full list shown in collapsible strip below the chart

export const EVENT_TYPES = {
  FOMC:    { label: "FOMC",    color: "#f03b3b", short: "FED",  impact: "high" },
  CPI:     { label: "CPI",     color: "#f5a623", short: "CPI",  impact: "high" },
  NFP:     { label: "NFP",     color: "#a78bfa", short: "NFP",  impact: "high" },
  FED:     { label: "Fed Speech", color: "#f03b3b", short: "SPK", impact: "medium" },
  PPI:     { label: "PPI",     color: "#4db8ff", short: "PPI",  impact: "medium" },
  PCE:     { label: "PCE",     color: "#f5a623", short: "PCE",  impact: "high" },
  RETAIL:  { label: "Retail Sales", color: "#4db8ff", short: "RET", impact: "medium" },
  GDP:     { label: "GDP",     color: "#00c896", short: "GDP",  impact: "high" },
};

// 2025-2026 calendar — times in ET (Eastern Time)
// ts = Unix ms timestamp
function et(dateStr, timeStr = "14:00") {
  const [h, m] = timeStr.split(":").map(Number);
  const d = new Date(`${dateStr}T${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:00-05:00`);
  return d.getTime();
}

export const ECONOMIC_EVENTS = [
  // ── FOMC 2025 ──────────────────────────────────────────────
  { id: "fomc-jan25",  type: "FOMC", ts: et("2025-01-29","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-mar25",  type: "FOMC", ts: et("2025-03-19","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-may25",  type: "FOMC", ts: et("2025-05-07","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-jun25",  type: "FOMC", ts: et("2025-06-18","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-jul25",  type: "FOMC", ts: et("2025-07-30","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-sep25",  type: "FOMC", ts: et("2025-09-17","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-oct25",  type: "FOMC", ts: et("2025-10-29","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-dec25",  type: "FOMC", ts: et("2025-12-17","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-jan26",  type: "FOMC", ts: et("2026-01-28","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "fomc-mar26",  type: "FOMC", ts: et("2026-03-18","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },

  // ── CPI 2025-2026 ──────────────────────────────────────────
  { id: "cpi-jan25",   type: "CPI",  ts: et("2025-01-15","08:30"), title: "CPI (Dec)",                detail: "Consumer Price Index — December 2024" },
  { id: "cpi-feb25",   type: "CPI",  ts: et("2025-02-12","08:30"), title: "CPI (Jan)",                detail: "Consumer Price Index — January 2025" },
  { id: "cpi-mar25",   type: "CPI",  ts: et("2025-03-12","08:30"), title: "CPI (Feb)",                detail: "Consumer Price Index — February 2025" },
  { id: "cpi-apr25",   type: "CPI",  ts: et("2025-04-10","08:30"), title: "CPI (Mar)",                detail: "Consumer Price Index — March 2025" },
  { id: "cpi-may25",   type: "CPI",  ts: et("2025-05-13","08:30"), title: "CPI (Apr)",                detail: "Consumer Price Index — April 2025" },
  { id: "cpi-jun25",   type: "CPI",  ts: et("2025-06-11","08:30"), title: "CPI (May)",                detail: "Consumer Price Index — May 2025" },
  { id: "cpi-jul25",   type: "CPI",  ts: et("2025-07-15","08:30"), title: "CPI (Jun)",                detail: "Consumer Price Index — June 2025" },
  { id: "cpi-aug25",   type: "CPI",  ts: et("2025-08-12","08:30"), title: "CPI (Jul)",                detail: "Consumer Price Index — July 2025" },
  { id: "cpi-sep25",   type: "CPI",  ts: et("2025-09-10","08:30"), title: "CPI (Aug)",                detail: "Consumer Price Index — August 2025" },
  { id: "cpi-oct25",   type: "CPI",  ts: et("2025-10-15","08:30"), title: "CPI (Sep)",                detail: "Consumer Price Index — September 2025" },
  { id: "cpi-nov25",   type: "CPI",  ts: et("2025-11-12","08:30"), title: "CPI (Oct)",                detail: "Consumer Price Index — October 2025" },
  { id: "cpi-dec25",   type: "CPI",  ts: et("2025-12-10","08:30"), title: "CPI (Nov)",                detail: "Consumer Price Index — November 2025" },
  { id: "cpi-jan26",   type: "CPI",  ts: et("2026-01-14","08:30"), title: "CPI (Dec)",                detail: "Consumer Price Index — December 2025" },
  { id: "cpi-feb26",   type: "CPI",  ts: et("2026-02-11","08:30"), title: "CPI (Jan)",                detail: "Consumer Price Index — January 2026" },
  { id: "cpi-mar26",   type: "CPI",  ts: et("2026-03-11","08:30"), title: "CPI (Feb)",                detail: "Consumer Price Index — February 2026" },

  // ── NFP 2025-2026 ──────────────────────────────────────────
  { id: "nfp-jan25",   type: "NFP",  ts: et("2025-01-10","08:30"), title: "NFP (Dec)",                detail: "Nonfarm Payrolls — December 2024" },
  { id: "nfp-feb25",   type: "NFP",  ts: et("2025-02-07","08:30"), title: "NFP (Jan)",                detail: "Nonfarm Payrolls — January 2025" },
  { id: "nfp-mar25",   type: "NFP",  ts: et("2025-03-07","08:30"), title: "NFP (Feb)",                detail: "Nonfarm Payrolls — February 2025" },
  { id: "nfp-apr25",   type: "NFP",  ts: et("2025-04-04","08:30"), title: "NFP (Mar)",                detail: "Nonfarm Payrolls — March 2025" },
  { id: "nfp-may25",   type: "NFP",  ts: et("2025-05-02","08:30"), title: "NFP (Apr)",                detail: "Nonfarm Payrolls — April 2025" },
  { id: "nfp-jun25",   type: "NFP",  ts: et("2025-06-06","08:30"), title: "NFP (May)",                detail: "Nonfarm Payrolls — May 2025" },
  { id: "nfp-jul25",   type: "NFP",  ts: et("2025-07-03","08:30"), title: "NFP (Jun)",                detail: "Nonfarm Payrolls — June 2025" },
  { id: "nfp-aug25",   type: "NFP",  ts: et("2025-08-01","08:30"), title: "NFP (Jul)",                detail: "Nonfarm Payrolls — July 2025" },
  { id: "nfp-sep25",   type: "NFP",  ts: et("2025-09-05","08:30"), title: "NFP (Aug)",                detail: "Nonfarm Payrolls — August 2025" },
  { id: "nfp-oct25",   type: "NFP",  ts: et("2025-10-03","08:30"), title: "NFP (Sep)",                detail: "Nonfarm Payrolls — September 2025" },
  { id: "nfp-nov25",   type: "NFP",  ts: et("2025-11-07","08:30"), title: "NFP (Oct)",                detail: "Nonfarm Payrolls — October 2025" },
  { id: "nfp-dec25",   type: "NFP",  ts: et("2025-12-05","08:30"), title: "NFP (Nov)",                detail: "Nonfarm Payrolls — November 2025" },
  { id: "nfp-jan26",   type: "NFP",  ts: et("2026-01-09","08:30"), title: "NFP (Dec)",                detail: "Nonfarm Payrolls — December 2025" },
  { id: "nfp-feb26",   type: "NFP",  ts: et("2026-02-06","08:30"), title: "NFP (Jan)",                detail: "Nonfarm Payrolls — January 2026" },
  { id: "nfp-mar26",   type: "NFP",  ts: et("2026-03-06","08:30"), title: "NFP (Feb)",                detail: "Nonfarm Payrolls — February 2026" },

  // ── PCE 2025-2026 ──────────────────────────────────────────
  { id: "pce-jan25",   type: "PCE",  ts: et("2025-01-31","08:30"), title: "PCE (Dec)",                detail: "Core PCE Price Index — December 2024" },
  { id: "pce-feb25",   type: "PCE",  ts: et("2025-02-28","08:30"), title: "PCE (Jan)",                detail: "Core PCE Price Index — January 2025" },
  { id: "pce-mar25",   type: "PCE",  ts: et("2025-03-28","08:30"), title: "PCE (Feb)",                detail: "Core PCE Price Index — February 2025" },
  { id: "pce-apr25",   type: "PCE",  ts: et("2025-04-30","08:30"), title: "PCE (Mar)",                detail: "Core PCE Price Index — March 2025" },
  { id: "pce-may25",   type: "PCE",  ts: et("2025-05-30","08:30"), title: "PCE (Apr)",                detail: "Core PCE Price Index — April 2025" },
  { id: "pce-jun25",   type: "PCE",  ts: et("2025-06-27","08:30"), title: "PCE (May)",                detail: "Core PCE Price Index — May 2025" },
  { id: "pce-jul25",   type: "PCE",  ts: et("2025-07-31","08:30"), title: "PCE (Jun)",                detail: "Core PCE Price Index — June 2025" },
  { id: "pce-aug25",   type: "PCE",  ts: et("2025-08-29","08:30"), title: "PCE (Jul)",                detail: "Core PCE Price Index — July 2025" },
  { id: "pce-sep25",   type: "PCE",  ts: et("2025-09-26","08:30"), title: "PCE (Aug)",                detail: "Core PCE Price Index — August 2025" },
  { id: "pce-oct25",   type: "PCE",  ts: et("2025-10-31","08:30"), title: "PCE (Sep)",                detail: "Core PCE Price Index — September 2025" },
  { id: "pce-nov25",   type: "PCE",  ts: et("2025-11-26","08:30"), title: "PCE (Oct)",                detail: "Core PCE Price Index — October 2025" },
  { id: "pce-dec25",   type: "PCE",  ts: et("2025-12-19","08:30"), title: "PCE (Nov)",                detail: "Core PCE Price Index — November 2025" },
  { id: "pce-jan26",   type: "PCE",  ts: et("2026-01-30","08:30"), title: "PCE (Dec)",                detail: "Core PCE Price Index — December 2025" },

  // ── PPI 2025-2026 ──────────────────────────────────────────
  { id: "ppi-jan25",   type: "PPI",  ts: et("2025-01-14","08:30"), title: "PPI (Dec)",                detail: "Producer Price Index — December 2024" },
  { id: "ppi-feb25",   type: "PPI",  ts: et("2025-02-13","08:30"), title: "PPI (Jan)",                detail: "Producer Price Index — January 2025" },
  { id: "ppi-mar25",   type: "PPI",  ts: et("2025-03-13","08:30"), title: "PPI (Feb)",                detail: "Producer Price Index — February 2025" },
  { id: "ppi-apr25",   type: "PPI",  ts: et("2025-04-11","08:30"), title: "PPI (Mar)",                detail: "Producer Price Index — March 2025" },
  { id: "ppi-may25",   type: "PPI",  ts: et("2025-05-15","08:30"), title: "PPI (Apr)",                detail: "Producer Price Index — April 2025" },
  { id: "ppi-jun25",   type: "PPI",  ts: et("2025-06-12","08:30"), title: "PPI (May)",                detail: "Producer Price Index — May 2025" },
  { id: "ppi-jul25",   type: "PPI",  ts: et("2025-07-16","08:30"), title: "PPI (Jun)",                detail: "Producer Price Index — June 2025" },
  { id: "ppi-aug25",   type: "PPI",  ts: et("2025-08-14","08:30"), title: "PPI (Jul)",                detail: "Producer Price Index — July 2025" },
  { id: "ppi-sep25",   type: "PPI",  ts: et("2025-09-11","08:30"), title: "PPI (Aug)",                detail: "Producer Price Index — August 2025" },
  { id: "ppi-oct25",   type: "PPI",  ts: et("2025-10-16","08:30"), title: "PPI (Sep)",                detail: "Producer Price Index — September 2025" },
  { id: "ppi-nov25",   type: "PPI",  ts: et("2025-11-13","08:30"), title: "PPI (Oct)",                detail: "Producer Price Index — October 2025" },
  { id: "ppi-dec25",   type: "PPI",  ts: et("2025-12-11","08:30"), title: "PPI (Nov)",                detail: "Producer Price Index — November 2025" },
  { id: "ppi-jan26",   type: "PPI",  ts: et("2026-01-15","08:30"), title: "PPI (Dec)",                detail: "Producer Price Index — December 2025" },

  // ── Retail Sales 2025-2026 ─────────────────────────────────
  { id: "ret-jan25",   type: "RETAIL", ts: et("2025-01-16","08:30"), title: "Retail Sales (Dec)",     detail: "US Retail Sales — December 2024" },
  { id: "ret-feb25",   type: "RETAIL", ts: et("2025-02-14","08:30"), title: "Retail Sales (Jan)",     detail: "US Retail Sales — January 2025" },
  { id: "ret-mar25",   type: "RETAIL", ts: et("2025-03-17","08:30"), title: "Retail Sales (Feb)",     detail: "US Retail Sales — February 2025" },
  { id: "ret-apr25",   type: "RETAIL", ts: et("2025-04-16","08:30"), title: "Retail Sales (Mar)",     detail: "US Retail Sales — March 2025" },
  { id: "ret-may25",   type: "RETAIL", ts: et("2025-05-15","08:30"), title: "Retail Sales (Apr)",     detail: "US Retail Sales — April 2025" },
  { id: "ret-jun25",   type: "RETAIL", ts: et("2025-06-17","08:30"), title: "Retail Sales (May)",     detail: "US Retail Sales — May 2025" },
  { id: "ret-jul25",   type: "RETAIL", ts: et("2025-07-16","08:30"), title: "Retail Sales (Jun)",     detail: "US Retail Sales — June 2025" },
  { id: "ret-aug25",   type: "RETAIL", ts: et("2025-08-15","08:30"), title: "Retail Sales (Jul)",     detail: "US Retail Sales — July 2025" },
  { id: "ret-sep25",   type: "RETAIL", ts: et("2025-09-16","08:30"), title: "Retail Sales (Aug)",     detail: "US Retail Sales — August 2025" },
  { id: "ret-oct25",   type: "RETAIL", ts: et("2025-10-17","08:30"), title: "Retail Sales (Sep)",     detail: "US Retail Sales — September 2025" },
  { id: "ret-nov25",   type: "RETAIL", ts: et("2025-11-14","08:30"), title: "Retail Sales (Oct)",     detail: "US Retail Sales — October 2025" },
  { id: "ret-dec25",   type: "RETAIL", ts: et("2025-12-16","08:30"), title: "Retail Sales (Nov)",     detail: "US Retail Sales — November 2025" },
  { id: "ret-jan26",   type: "RETAIL", ts: et("2026-01-16","08:30"), title: "Retail Sales (Dec)",     detail: "US Retail Sales — December 2025" },

  // ── Fed Speeches / Jackson Hole ────────────────────────────
  { id: "jh-aug25",   type: "FED",  ts: et("2025-08-22","10:00"), title: "Jackson Hole Symposium",   detail: "Powell keynote — Jackson Hole Economic Symposium" },
  { id: "fed-feb25",  type: "FED",  ts: et("2025-02-11","15:00"), title: "Powell Testimony",         detail: "Fed Chair Powell testimony to Congress" },
  { id: "fed-mar25",  type: "FED",  ts: et("2025-03-07","10:00"), title: "Fed Speech",               detail: "Federal Reserve Governor speech" },
  { id: "fed-may25",  type: "FED",  ts: et("2025-05-15","13:30"), title: "Fed Speech",               detail: "Federal Reserve Governor speech" },
  { id: "fed-jun25b", type: "FED",  ts: et("2025-06-25","10:00"), title: "Fed Speech",               detail: "Federal Reserve Governor speech" },
  { id: "fed-sep25b", type: "FED",  ts: et("2025-09-25","10:00"), title: "Fed Speech",               detail: "Federal Reserve Governor speech" },
  { id: "jh-aug26",   type: "FED",  ts: et("2026-08-21","10:00"), title: "Jackson Hole Symposium",   detail: "Powell keynote — Jackson Hole Economic Symposium 2026" },
  // ── Additional 2026 events ────────────────────────────────
  { id: "fomc-feb26",  type: "FOMC", ts: et("2026-02-18","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },
  { id: "pce-feb26",   type: "PCE",  ts: et("2026-02-27","08:30"), title: "PCE (Jan)",                detail: "Core PCE Price Index — January 2026" },
  { id: "pce-mar26",   type: "PCE",  ts: et("2026-03-27","08:30"), title: "PCE (Feb)",                detail: "Core PCE Price Index — February 2026" },
  { id: "ppi-feb26",   type: "PPI",  ts: et("2026-02-12","08:30"), title: "PPI (Jan)",                detail: "Producer Price Index — January 2026" },
  { id: "ppi-mar26",   type: "PPI",  ts: et("2026-03-12","08:30"), title: "PPI (Feb)",                detail: "Producer Price Index — February 2026" },
  { id: "ret-feb26",   type: "RETAIL", ts: et("2026-02-13","08:30"), title: "Retail Sales (Jan)",     detail: "US Retail Sales — January 2026" },
  { id: "ret-mar26",   type: "RETAIL", ts: et("2026-03-17","08:30"), title: "Retail Sales (Feb)",     detail: "US Retail Sales — February 2026" },
  { id: "nfp-apr26",   type: "NFP",  ts: et("2026-04-03","08:30"), title: "NFP (Mar)",                detail: "Nonfarm Payrolls — March 2026" },
  { id: "cpi-apr26",   type: "CPI",  ts: et("2026-04-10","08:30"), title: "CPI (Mar)",                detail: "Consumer Price Index — March 2026" },
  { id: "fomc-apr26",  type: "FOMC", ts: et("2026-04-29","14:00"), title: "FOMC Rate Decision",       detail: "Fed funds rate decision + press conference" },

];

// ─── ECONOMIC CALENDAR STRIP ──────────────────────────────────
// Shown below the chart — lists upcoming events with countdown
export function EconomicCalendarStrip({ enabledTypes = Object.keys(EVENT_TYPES), onToggleType }) {
  const [expanded, setExpanded] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);

  const now = Date.now();

  const filtered = useMemo(() =>
    ECONOMIC_EVENTS
      .filter(e => enabledTypes.includes(e.type))
      .sort((a, b) => a.ts - b.ts),
    [enabledTypes]
  );

  // Split into past (last 5) and upcoming (next 10)
  const upcoming = filtered.filter(e => e.ts >= now - 3600000).slice(0, 12);
  const next = upcoming[0];

  const fmtCountdown = (ts) => {
    const diff = ts - now;
    if (diff < 0) return "NOW";
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const fmtDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) + " ET";
  };

  const isPast = (ts) => ts < now;

  return (
    <div style={{
      background: "#0a0c10", borderTop: "1px solid #1e2130",
      flexShrink: 0, userSelect: "none",
    }}>
      {/* Header bar */}
      <div style={{
        height: 30, display: "flex", alignItems: "center",
        padding: "0 10px", gap: 8, borderBottom: expanded ? "1px solid #1e2130" : "none",
        cursor: "pointer",
      }} onClick={() => setExpanded(e => !e)}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#4a4f5e", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          📅 Economic Calendar
        </span>

        {/* Next event pill */}
        {next && (
          <div style={{
            display: "flex", alignItems: "center", gap: 5,
            background: "rgba(0,0,0,0.3)", border: `1px solid ${EVENT_TYPES[next.type].color}44`,
            borderRadius: 3, padding: "1px 7px",
          }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: EVENT_TYPES[next.type].color }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: EVENT_TYPES[next.type].color }}>
              {EVENT_TYPES[next.type].short}
            </span>
            <span style={{ fontSize: 9, color: "#5a5f6e" }}>{next.title}</span>
            <span style={{ fontSize: 9, fontFamily: "monospace", color: "#7a7f8e" }}>in {fmtCountdown(next.ts)}</span>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Type filter pills */}
        <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
          {Object.entries(EVENT_TYPES).map(([key, ev]) => (
            <div key={key} onClick={() => onToggleType(key)} style={{
              padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700,
              cursor: "pointer", letterSpacing: "0.05em",
              background: enabledTypes.includes(key) ? `${ev.color}22` : "transparent",
              border: `1px solid ${enabledTypes.includes(key) ? ev.color + "66" : "#2a2d3a"}`,
              color: enabledTypes.includes(key) ? ev.color : "#3a3d4a",
            }}>{ev.short}</div>
          ))}
        </div>

        <span style={{ fontSize: 10, color: "#3a3d4a", marginLeft: 4 }}>
          {expanded ? "▼" : "▶"}
        </span>
      </div>

      {/* Event list */}
      {expanded && (
        <div style={{
          display: "flex", overflowX: "auto", padding: "6px 8px", gap: 6,
          scrollbarWidth: "none",
        }}>
          {upcoming.length === 0 && (
            <div style={{ fontSize: 10, color: "#3a3d4a", padding: "4px 8px" }}>No upcoming events</div>
          )}
          {upcoming.map(ev => {
            const evType = EVENT_TYPES[ev.type];
            const past = isPast(ev.ts);
            const isHovered = hoveredId === ev.id;
            const isNext = ev.id === next?.id;
            return (
              <div key={ev.id}
                onMouseEnter={() => setHoveredId(ev.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  flexShrink: 0, width: 125, background: isHovered ? "#111318" : "#0d0f14",
                  border: `1px solid ${isNext ? evType.color + "55" : past ? "#1a1d27" : "#1e2130"}`,
                  borderRadius: 4, padding: "5px 7px", cursor: "default",
                  opacity: past ? 0.45 : 1,
                  position: "relative",
                  boxShadow: isNext ? `0 0 8px ${evType.color}22` : "none",
                }}>
                {/* Top row: type badge + countdown */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
                    color: evType.color, background: evType.color + "18",
                    padding: "1px 4px", borderRadius: 2,
                  }}>{evType.short}</span>
                  <span style={{
                    fontSize: 8, fontFamily: "monospace",
                    color: past ? "#3a3d4a" : isNext ? evType.color : "#5a5f6e",
                    fontWeight: isNext ? 700 : 400,
                  }}>
                    {past ? "PAST" : fmtCountdown(ev.ts)}
                  </span>
                </div>
                {/* Title */}
                <div style={{ fontSize: 10, fontWeight: 600, color: past ? "#3a3d4a" : "#a0a5b4", marginBottom: 2, lineHeight: 1.3 }}>
                  {ev.title}
                </div>
                {/* Date */}
                <div style={{ fontSize: 9, fontFamily: "monospace", color: "#3a3d4a" }}>
                  {fmtDate(ev.ts)}
                </div>
                {/* Impact dot */}
                <div style={{
                  position: "absolute", top: 5, right: isNext ? 22 : 5,
                  width: 4, height: 4, borderRadius: "50%",
                  background: evType.impact === "high" ? evType.color : evType.color + "77",
                }} />
                {/* Next badge */}
                {isNext && (
                  <div style={{
                    position: "absolute", top: 3, right: 5,
                    fontSize: 7, fontWeight: 800, color: evType.color,
                    letterSpacing: "0.05em",
                  }}>▶</div>
                )}
                {/* Hover detail */}
                {isHovered && !past && (
                  <div style={{
                    position: "absolute", bottom: "calc(100% + 4px)", left: 0, zIndex: 1000,
                    background: "#111318", border: `1px solid ${evType.color}44`,
                    borderRadius: 4, padding: "6px 8px", width: 180,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.8)",
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: evType.color, marginBottom: 3 }}>{ev.title}</div>
                    <div style={{ fontSize: 9, color: "#7a7f8e", lineHeight: 1.4 }}>{ev.detail}</div>
                    <div style={{ fontSize: 8, fontFamily: "monospace", color: "#4a4f5e", marginTop: 4 }}>{fmtDate(ev.ts)}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
